'use client';

import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { ContentCopyIcon } from '@/shared/ui/display/EmojiIcon';

export interface InfoRowProps {
  label: string;
  value: string;
  onCopy?: () => void;
  /** 값을 링크로 렌더 (예: `tel:01012345678`) */
  href?: string;
  multiline?: boolean;
}

/**
 * 라벨/값 정보 행 (복사 버튼 또는 링크 옵션)
 */
export function InfoRow({ label, value, onCopy, href, multiline = false }: InfoRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: 0.5,
        minHeight: 28,
        '&:not(:last-of-type)': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          minWidth: 68,
          flexShrink: 0,
          fontSize: '0.75rem',
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component={href ? 'a' : 'p'}
        href={href}
        sx={{
          flex: 1,
          wordBreak: multiline ? 'break-word' : 'break-all',
          whiteSpace: multiline ? 'pre-wrap' : undefined,
          fontSize: '0.8125rem',
          lineHeight: 1.4,
          ...(href
            ? {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }
            : {}),
        }}
      >
        {value}
      </Typography>
      {onCopy && (
        <Tooltip title="복사">
          <IconButton size="small" onClick={onCopy} sx={{ p: 0.25, mt: -0.25 }} aria-label="복사">
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
